import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { movimientosAPI, clientesAPI, whatsappTemplatesAPI, whatsappEnviosAPI, whatsappBaileysAPI, recibosCobroAPI, recibosCobroGrupoAPI, pagosAPI, movimientosPredefinidosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { WhatsAppServicioPanel } from '../components/WhatsAppServicioPanel';
import { PlantillaImagenEditor } from '../components/PlantillaImagenEditor';
import {
  readUseBaileysFromStorage,
  writeUseBaileysToStorage,
  abrirWhatsappWeb,
} from '../utils/whatsappBaileys';
import '../Clientes.css';
import '../Movimientos.css';
import { sortMovimientosCronologico, deltaSaldoMovimiento } from '../utils/movimientosOrden';
import { FacturaMovimientoMarca } from '../components/FacturaMovimientoMarca';
import {
  imprimirComprobanteMovimiento,
  imprimirReporteMovimientos,
  movimientoTieneRecibo,
  movimientoTieneReciboGrupo,
  movimientoTienePago,
} from '../utils/impresionMovimientos';
import { useMovimientoDetalle } from '../hooks/useMovimientoDetalle';
import { MovimientoDetalleModal } from '../components/MovimientoDetalleModal';
import { MovimientoConceptoCell } from '../components/MovimientoConceptoCell';
import FiltroPredefinidosMultiselect from '../components/FiltroPredefinidosMultiselect';
import { ExportActions } from '../components/ExportActions';
import {
  exportFromColumnas,
  valorCeldaMovimientoExport,
  COLUMNAS_MOVIMIENTOS_CUENTA,
} from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';

const formatearTelefono = (telefono) => {
  if (!telefono) return '';
  let n = String(telefono).replace(/[\s\-()]/g, '');
  if (n.startsWith('0')) n = n.substring(1);
  if (!n.startsWith('+') && !n.startsWith('54')) n = '54' + n;
  if (!n.startsWith('+')) n = '+' + n;
  return n;
};

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const getSortValueClienteMov = (mov, colId) => {
  switch (colId) {
    case 'fecha':
      return new Date(mov.fecha || 0).getTime() || 0;
    case 'concepto':
      return (mov.concepto || '').toString().toLowerCase();
    case 'debito':
      return mov.tipo_movimiento === 'DEBITO' ? (parseFloat(mov.monto) || 0) : 0;
    case 'credito':
      return mov.tipo_movimiento === 'CREDITO' ? (parseFloat(mov.monto) || 0) : 0;
    case 'saldo':
      return parseFloat(mov.saldo_acumulado || 0) || 0;
    default:
      return '';
  }
};

const ClienteMovimientos = () => {
  const { id } = useParams();
  const [cliente, setCliente] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const primeraCargaMovRef = useRef(true);
  const [refrescando, setRefrescando] = useState(false);
  const cargaMovSeqRef = useRef(0);
  const [filtros, setFiltros] = useState(() =>
    loadFiltrosSession(
      id ? `cliente_${id}_movimientos_filtros` : 'cliente_movimientos_filtros',
      { fecha_desde: '', fecha_hasta: '', movimiento_predefinido_ids: [] }
    )
  );
  const fechaDefaultAplicadaRef = useRef(false);
  const [predefinidos, setPredefinidos] = useState([]);
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [saldoFiltrado, setSaldoFiltrado] = useState(null);
  const [saldoCuenta, setSaldoCuenta] = useState(null);
  const [saldoInicial, setSaldoInicial] = useState(null);
  const { hasPermission } = useAuth();
  const { formatFecha: formatFechaSistema, formatFechaHora, fechaHoy } = useZonaHoraria();
  const { logoUrl } = useBranding();
  const [showModalWhatsApp, setShowModalWhatsApp] = useState(false);
  const [fechaSaldoWa, setFechaSaldoWa] = useState('');
  const [saldoWa, setSaldoWa] = useState(null);

  useEffect(() => {
    if (!id) return;
    setFiltros(
      loadFiltrosSession(`cliente_${id}_movimientos_filtros`, {
        fecha_desde: '',
        fecha_hasta: '',
        movimiento_predefinido_ids: [],
      })
    );
    fechaDefaultAplicadaRef.current = false;
    primeraCargaMovRef.current = true;
  }, [id]);

  useEffect(() => {
    if (!id) return;
    saveFiltrosSession(`cliente_${id}_movimientos_filtros`, filtros);
  }, [id, filtros]);

  useEffect(() => {
    if (showModalWhatsApp && !fechaSaldoWa) setFechaSaldoWa(fechaHoy());
  }, [showModalWhatsApp, fechaSaldoWa, fechaHoy]);

  useEffect(() => {
    if (!showModalWhatsApp || !fechaSaldoWa || !id) {
      setSaldoWa(null);
      return;
    }
    clientesAPI.saldosAl([parseInt(id, 10)], fechaSaldoWa)
      .then((res) => {
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          setSaldoWa(res.data[0].saldo);
        } else {
          setSaldoWa(null);
        }
      })
      .catch(() => setSaldoWa(null));
  }, [showModalWhatsApp, fechaSaldoWa, id]);

  useEffect(() => {
    if (fechaDefaultAplicadaRef.current) return;
    fechaDefaultAplicadaRef.current = true;
    setFiltros((prev) => (prev.fecha_desde ? prev : { ...prev, fecha_desde: fechaHoy() }));
  }, [fechaHoy]);
  const {
    detalleMovimiento,
    detalleRecibo,
    detalleReciboGrupo,
    detallePago,
    detallePagoError,
    detalleLoading,
    abrirDetalle,
    cerrarDetalle,
  } = useMovimientoDetalle();

  const loadCliente = useCallback(async () => {
    if (!id) return;
    try {
      const response = await clientesAPI.getById(id);
      if (response.success) setCliente(response.data);
    } catch (error) {
      toast.error('Error al cargar cliente');
    }
  }, [id]);

  const loadPredefinidos = useCallback(async () => {
    try {
      const res = await movimientosPredefinidosAPI.getAll({ activo: true, uso: 'clientes', para_filtro: '1' });
      if (res.success && Array.isArray(res.data)) setPredefinidos(res.data);
    } catch {
      setPredefinidos([]);
    }
  }, []);

  useEffect(() => {
    loadPredefinidos();
  }, [loadPredefinidos]);

  const loadMovimientos = useCallback(async () => {
    if (!id) return;
    const mostrarLoaderPantalla = primeraCargaMovRef.current;
    const seq = ++cargaMovSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescando(true);
      const params = {};
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      if (filtros.movimiento_predefinido_ids?.length) {
        params.movimiento_predefinido_ids = filtros.movimiento_predefinido_ids.join(',');
      }
      if (filtros.fecha_desde || filtros.fecha_hasta || filtros.movimiento_predefinido_ids?.length) {
        params.incluir_resumen_saldos = '1';
      }
      const response = await movimientosAPI.getByCliente(id, params);
      if (seq !== cargaMovSeqRef.current) return;
      if (response.success) {
        setMovimientos(Array.isArray(response.data) ? response.data : []);
        setSaldoFiltrado(response.saldo_filtrado !== undefined ? response.saldo_filtrado : null);
        setSaldoCuenta(response.saldo_cuenta !== undefined ? response.saldo_cuenta : null);
        setSaldoInicial(response.saldo_inicial !== undefined ? response.saldo_inicial : null);
      } else {
        setMovimientos([]);
        setSaldoFiltrado(null);
        setSaldoCuenta(null);
        setSaldoInicial(null);
      }
    } catch (error) {
      if (seq !== cargaMovSeqRef.current) return;
      setMovimientos([]);
      setSaldoFiltrado(null);
      setSaldoCuenta(null);
      setSaldoInicial(null);
      toast.error(error.message || 'Error al cargar movimientos');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaMovRef.current = false;
      }
      if (seq === cargaMovSeqRef.current) setRefrescando(false);
    }
  }, [id, filtros.fecha_desde, filtros.fecha_hasta, filtros.movimiento_predefinido_ids]);

  useEffect(() => {
    loadCliente();
  }, [loadCliente]);

  useEffect(() => {
    loadMovimientos();
  }, [loadMovimientos]);

  const formatFecha = formatFechaSistema;

  const formatMoneda = (n) =>
    parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSeleccionadaId, setPlantillaSeleccionadaId] = useState('');
  const [mensajePersonalizado, setMensajePersonalizado] = useState('');
  const [guardarEnHistorial, setGuardarEnHistorial] = useState(true);
  const [waUseBaileys, setWaUseBaileys] = useState(() => readUseBaileysFromStorage());
  const [waStatus, setWaStatus] = useState(null);
  const [waStatusLoading, setWaStatusLoading] = useState(false);
  const [anulandoComprobante, setAnulandoComprobante] = useState(false);

  const refreshWaStatus = useCallback(async () => {
    if (!waUseBaileys) return;
    setWaStatusLoading(true);
    try {
      const s = await whatsappBaileysAPI.getStatus();
      setWaStatus(s);
    } catch (e) {
      setWaStatus({ reachable: false, connected: false, serviceError: e.message });
    } finally {
      setWaStatusLoading(false);
    }
  }, [waUseBaileys]);

  const handleToggleWaBaileys = (v) => {
    writeUseBaileysToStorage(v);
    setWaUseBaileys(v);
    if (v) setTimeout(() => refreshWaStatus(), 0);
  };

  useEffect(() => {
    if (showModalWhatsApp && waUseBaileys) refreshWaStatus();
  }, [showModalWhatsApp, waUseBaileys, refreshWaStatus]);

  const hayFiltrosActivos = !!(filtros.fecha_desde || filtros.fecha_hasta || filtros.movimiento_predefinido_ids?.length);

  const saldoCuentaReal = useMemo(() => {
    if (saldoCuenta !== null) return saldoCuenta;
    const porFecha = [...movimientos].sort(sortMovimientosCronologico);
    let saldo = 0;
    porFecha.forEach((m) => { saldo += deltaSaldoMovimiento(m); });
    return saldo;
  }, [movimientos, saldoCuenta]);

  const movimientosOrdenados = useMemo(() => {
    if (!sortByColumn) return [...movimientos].sort(sortMovimientosCronologico);
    if (sortByColumn === 'saldo') {
      const porFecha = [...movimientos].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
      let s = 0;
      const conSaldo = porFecha.map(m => {
        s += m.tipo_movimiento === 'DEBITO' ? (parseFloat(m.monto) || 0) : -(parseFloat(m.monto) || 0);
        return { ...m, _saldoParaOrdenar: s };
      });
      conSaldo.sort((a, b) => {
        const v = (a._saldoParaOrdenar || 0) - (b._saldoParaOrdenar || 0);
        return sortAsc ? v : -v;
      });
      return conSaldo;
    }
    const arr = [...movimientos];
    arr.sort((a, b) => {
      const v1 = getSortValueClienteMov(a, sortByColumn);
      const v2 = getSortValueClienteMov(b, sortByColumn);
      const num = typeof v1 === 'number' && typeof v2 === 'number';
      let cmp = 0;
      if (num) cmp = v1 - v2;
      else cmp = String(v1).localeCompare(String(v2), 'es');
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [movimientos, sortByColumn, sortAsc]);

  const movimientosParaMostrar = useMemo(() => {
    const base = saldoInicial != null ? Number(saldoInicial) : 0;
    const porFecha = [...movimientos].sort(sortMovimientosCronologico);
    const saldoPorId = {};
    let saldo = base;
    porFecha.forEach((m) => {
      saldo += deltaSaldoMovimiento(m);
      saldoPorId[m.id] = saldo;
    });
    return movimientosOrdenados.map((m) => ({ ...m, saldo_acumulado: saldoPorId[m.id] ?? base }));
  }, [movimientosOrdenados, movimientos, saldoInicial]);

  const saldoCierreReporte = useMemo(() => {
    if (!movimientos.length) return saldoCuentaReal ?? 0;
    const base = saldoInicial != null ? Number(saldoInicial) : 0;
    return movimientos.reduce((acc, m) => acc + deltaSaldoMovimiento(m), base);
  }, [movimientos, saldoInicial, saldoCuentaReal]);

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) {
      setSortAsc(prev => !prev);
    } else {
      setSortByColumn(columnId);
      setSortAsc(true);
    }
  };

  const buildListaMovimientos = (movs) => {
    if (!Array.isArray(movs) || movs.length === 0) return 'Sin movimientos en el período.';
    return movs.map(m => {
      const fecha = formatFecha(m.fecha);
      const debe = m.tipo_movimiento === 'DEBITO' ? `$${formatMoneda(m.monto)}` : '-';
      const haber = m.tipo_movimiento === 'CREDITO' ? `$${formatMoneda(m.monto)}` : '-';
      return `${fecha} | ${(m.concepto || '').slice(0, 40)} | Debe: ${debe} | Haber: ${haber} | Saldo: $${formatMoneda(m.saldo_acumulado)}`;
    }).join('\n');
  };

  const aplicarPlantillaResumenMovimientos = (mensaje) => {
    if (!cliente) return mensaje;
    const saldo = saldoWa != null ? formatMoneda(saldoWa) : formatMoneda(saldoCierreReporte);
    const fechaSaldoFmt = fechaSaldoWa ? formatFecha(fechaSaldoWa) : '';
    const lista_movimientos = buildListaMovimientos(movimientosParaMostrar);
    return mensaje
      .replace(/{razon_social}/g, cliente.razon_social || '')
      .replace(/{cuit}/g, cliente.cuit || '')
      .replace(/{saldo}/g, saldo)
      .replace(/{fecha_saldo}/g, fechaSaldoFmt)
      .replace(/{lista_movimientos}/g, lista_movimientos)
      .replace(/{fecha_desde}/g, filtros.fecha_desde || 'Todo')
      .replace(/{fecha_hasta}/g, filtros.fecha_hasta || 'Todo')
      .replace(/{tipo_nombre}/g, cliente.tipo_nombre || '');
  };

  const loadPlantillas = useCallback(async () => {
    try {
      const res = await whatsappTemplatesAPI.getAll({ tipo: 'cliente' });
      if (res.success && Array.isArray(res.data)) setPlantillas(res.data);
    } catch (e) {
      setPlantillas([]);
    }
  }, []);

  const abrirModalWhatsApp = useCallback(async () => {
    if (!cliente || !(cliente.celular || cliente.telefono)) {
      toast.warning('Este cliente no tiene teléfono o celular registrado.');
      return;
    }
    await loadPlantillas();
    const res = await whatsappTemplatesAPI.getAll({ tipo: 'cliente' });
    const textoDefault = 'Hola {razon_social},\n\nResumen de movimientos de cuenta:\nCUIT: {cuit}\nPeríodo: {fecha_desde} a {fecha_hasta}\n\nDetalle:\n{lista_movimientos}\n\nSaldo actual: {saldo}\n\nSaludos.';
    if (res.success && res.data?.length > 0) {
      const primera = res.data[0];
      setPlantillaSeleccionadaId(primera.id);
      setMensajePersonalizado(primera.texto || textoDefault);
    } else {
      setPlantillaSeleccionadaId('');
      setMensajePersonalizado(textoDefault);
    }
    setShowModalWhatsApp(true);
  }, [cliente, loadPlantillas]);

  const seleccionarPlantilla = (idStr) => {
    const id = idStr ? parseInt(idStr, 10) : NaN;
    setPlantillaSeleccionadaId(Number.isFinite(id) ? id : '');
    const p = plantillas.find(x => x.id === id);
    if (p) setMensajePersonalizado(p.texto || '');
  };

  const enviarResumenWhatsApp = async () => {
    const telefono = cliente?.celular || cliente?.telefono;
    if (!telefono) {
      toast.warning('Cliente sin teléfono.');
      return;
    }
    const mensajeFinal = aplicarPlantillaResumenMovimientos(mensajePersonalizado);
    if (guardarEnHistorial) {
      try {
        await whatsappEnviosAPI.create({
          cliente_id: parseInt(id, 10),
          template_id: plantillaSeleccionadaId || null,
          mensaje_texto: mensajeFinal,
        });
      } catch (e) {
        console.error('No se pudo guardar en historial:', e);
      }
    }
    const num = formatearTelefono(telefono).replace('+', '');
    if (waUseBaileys) {
      if (!waStatus?.reachable) {
        toast.error('No se alcanza el servicio WhatsApp.');
        return;
      }
      if (!waStatus?.connected) {
        toast.warning('WhatsApp no está conectado. Escanee el QR en el servidor.');
        return;
      }
      try {
        await whatsappBaileysAPI.sendBatch([
          {
            phone: num,
            caption: mensajeFinal,
            templateId: plantillaSeleccionadaId || undefined,
            clienteId: parseInt(id, 10),
            destinatario: cliente?.razon_social,
          },
        ]);
        setShowModalWhatsApp(false);
        toast.success('Mensaje encolado para envío por WhatsApp.');
        return;
      } catch (e) {
        toast.error(e.message || 'Error al enviar por Baileys');
        return;
      }
    }
    abrirWhatsappWeb(num, mensajeFinal);
    setShowModalWhatsApp(false);
    toast.success('Se abrió WhatsApp con el mensaje.');
  };

  const generarPDF = () => {
    const fechaReporte = new Date();
    const fechaReporteStr = `${fechaReporte.getDate()} de ${MESES_ES[fechaReporte.getMonth()]} de ${fechaReporte.getFullYear()}`;
    const periodoHtml =
      filtros.fecha_desde || filtros.fecha_hasta
        ? `<p><strong>Período:</strong> ${filtros.fecha_desde || '...'} a ${filtros.fecha_hasta || '...'}</p>`
        : '';

    imprimirReporteMovimientos({
      titulo: 'Movimientos de cuenta corriente',
      subtitulo: `Reporte generado el ${fechaReporteStr}`,
      entidadInfoHtml: `
        <p><strong>Cliente:</strong> ${(cliente?.razon_social || '').replace(/</g, '&lt;')}</p>
        <p><strong>CUIT:</strong> ${cliente?.cuit || '-'}</p>
        ${cliente?.tipo_nombre ? `<p><strong>Tipo:</strong> ${cliente.tipo_nombre}</p>` : ''}
      `,
      periodoHtml,
      movimientos: movimientosParaMostrar,
      formatFecha,
      saldoFinal: saldoCierreReporte,
      logoUrl,
    });
  };

  const handleExportListado = async (format) => {
    try {
      const nombre = cliente?.razon_social ? `movimientos_cliente_${cliente.razon_social}` : 'movimientos_cliente';
      await exportFromColumnas({
        filename: nombre,
        sheetName: 'Movimientos',
        columnDefs: COLUMNAS_MOVIMIENTOS_CUENTA,
        visibleColumnIds: COLUMNAS_MOVIMIENTOS_CUENTA.map((c) => c.id),
        items: movimientosParaMostrar,
        getCellValue: (mov, colId) => valorCeldaMovimientoExport(mov, colId, formatFecha),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const handleImprimirFila = async (mov) => {
    try {
      const ok = await imprimirComprobanteMovimiento(mov, formatFecha, logoUrl);
      if (!ok) toast.error('No se pudo imprimir el comprobante');
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  const handleAnularRecibo = async () => {
    if (!detalleRecibo?.id || detalleRecibo.anulado_at) return;
    if (!window.confirm(`¿Anular el Recibo de Cobro N° ${detalleRecibo.numero_recibo}? Esta acción revierte el movimiento y los fondos asociados.`)) return;
    setAnulandoComprobante(true);
    try {
      const res = await recibosCobroAPI.anular(detalleRecibo.id);
      if (res.success) {
        toast.success(res.message || 'Recibo anulado');
        cerrarDetalle();
        loadMovimientos();
      } else {
        toast.error(res.message || 'No se pudo anular');
      }
    } catch (error) {
      toast.error(error.message || 'Error al anular recibo');
    } finally {
      setAnulandoComprobante(false);
    }
  };

  const handleAnularReciboGrupo = async () => {
    if (!detalleReciboGrupo?.id || detalleReciboGrupo.anulado_at) return;
    if (!window.confirm(`¿Anular el Recibo de Cobro Grupo N° ${detalleReciboGrupo.numero_recibo}? Esta acción revierte los créditos de todos los clientes y los fondos asociados.`)) return;
    setAnulandoComprobante(true);
    try {
      const res = await recibosCobroGrupoAPI.anular(detalleReciboGrupo.id);
      if (res.success) {
        toast.success(res.message || 'Recibo de grupo anulado');
        cerrarDetalle();
        loadMovimientos();
      } else {
        toast.error(res.message || 'No se pudo anular');
      }
    } catch (error) {
      toast.error(error.message || 'Error al anular recibo de grupo');
    } finally {
      setAnulandoComprobante(false);
    }
  };

  const handleAnularPago = async () => {
    if (!detallePago?.id || detallePago.anulado_at) return;
    const esPagoCuenta = detallePago.cliente_id && !detallePago.proveedor_id;
    const etiqueta = esPagoCuenta ? 'Pago a cuenta' : 'Orden de Pago';
    const numero = esPagoCuenta ? detallePago.numero_pago_cuenta : detallePago.numero_pago;
    if (!window.confirm(`¿Anular el ${etiqueta} N° ${numero}? Esta acción revierte el movimiento y los fondos asociados.`)) return;
    setAnulandoComprobante(true);
    try {
      const res = await pagosAPI.anular(detallePago.id);
      if (res.success) {
        toast.success(res.message || `${etiqueta} anulado`);
        cerrarDetalle();
        loadMovimientos();
      } else {
        toast.error(res.message || 'No se pudo anular');
      }
    } catch (error) {
      toast.error(error.message || 'Error al anular pago');
    } finally {
      setAnulandoComprobante(false);
    }
  };

  if (!id) {
    return (
      <div className="clientes">
        <p>Cliente no especificado.</p>
        <Link to="/clientes">Volver a Clientes</Link>
      </div>
    );
  }

  return (
    <div className="movimientos pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Movimientos del cliente</h1>
        <div className="page-header-acciones">
          <Link to="/clientes" className="btn-secondary">← Volver a clientes</Link>
          {hasPermission('ver_movimientos') && movimientos.length > 0 && (
            <ExportActions
              onCsv={() => handleExportListado('csv')}
              onExcel={() => handleExportListado('excel')}
              onPrint={generarPDF}
              printLabel="Generar reporte PDF"
            />
          )}
        </div>
      </div>

      <div className="pagina-listado-fijo">
      {cliente && (
        <div style={{
          padding: '14px',
          background: '#f0f4f8',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #ddd'
        }}>
          <strong>{cliente.razon_social}</strong>
          {cliente.cuit && <span style={{ marginLeft: '12px', color: '#666' }}>CUIT: {cliente.cuit}</span>}
          {cliente.tipo_nombre && (
            <span style={{ marginLeft: '12px', padding: '2px 8px', background: '#e8f4f8', borderRadius: '4px', fontSize: '12px' }}>
              {cliente.tipo_nombre}
            </span>
          )}
          {cliente.condicion_impositiva && <span style={{ marginLeft: '12px', color: '#666' }}>Cond.: {cliente.condicion_impositiva}</span>}
          {cliente.ingresos_brutos && <span style={{ marginLeft: '12px', color: '#666' }}>IIBB: {cliente.ingresos_brutos}</span>}
          {cliente.drg && <span style={{ marginLeft: '12px', color: '#666' }}>DRG: {cliente.drg}</span>}
          {cliente.dv && <span style={{ marginLeft: '12px', color: '#666' }}>DV: {cliente.dv}</span>}
          {(cliente.iva_liquida === 1 || cliente.iva_liquida === 0 || cliente.iva_liquida === '1' || cliente.iva_liquida === '0') && (
            <span style={{ marginLeft: '12px', color: '#666' }}>IVA líquida: {(cliente.iva_liquida === 1 || cliente.iva_liquida === '1') ? 'Sí' : 'No'}</span>
          )}
          {!hayFiltrosActivos && (
            <span style={{ marginLeft: '12px', fontWeight: 'bold' }}>
              Saldo de la cuenta: ${formatMoneda(saldoCuentaReal)}
            </span>
          )}
        </div>
      )}

      {hayFiltrosActivos && (saldoFiltrado !== null || saldoCuenta !== null || saldoInicial !== null) && (
        <div style={{ marginBottom: 16, padding: 12, background: '#f0f4f8', borderRadius: 8, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {saldoFiltrado !== null && (
            <div>
              <strong>Saldo de los movimientos filtrados</strong>:{' '}
              <span className={saldoFiltrado > 0 ? 'negative' : saldoFiltrado < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoFiltrado)}
              </span>
            </div>
          )}
          {saldoCuenta !== null && (
            <div>
              <strong>Saldo acumulado de la cuenta (sin filtros)</strong>:{' '}
              <span className={saldoCuenta > 0 ? 'negative' : saldoCuenta < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoCuenta)}
              </span>
            </div>
          )}
          {saldoInicial !== null && (
            <div>
              <strong>Saldo inicial</strong> {filtros.fecha_desde && `(al ${formatFecha(filtros.fecha_desde)}): `}
              <span className={saldoInicial > 0 ? 'negative' : saldoInicial < 0 ? 'positive' : ''}>
                ${formatMoneda(saldoInicial)}
              </span>
            </div>
          )}
        </div>
      )}
      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center' }}>
          <input
            type="date"
            name="fecha_desde"
            value={filtros.fecha_desde}
            onChange={e => setFiltros(prev => ({ ...prev, fecha_desde: e.target.value }))}
            placeholder="Fecha desde"
          />
          <input
            type="date"
            name="fecha_hasta"
            value={filtros.fecha_hasta}
            onChange={e => setFiltros(prev => ({ ...prev, fecha_hasta: e.target.value }))}
            placeholder="Fecha hasta"
          />
          <FiltroPredefinidosMultiselect
            predefinidos={predefinidos}
            selectedIds={filtros.movimiento_predefinido_ids}
            onChange={(ids) => setFiltros((prev) => ({ ...prev, movimiento_predefinido_ids: ids }))}
            label="Tipo de movimiento"
          />
          {cliente && (
            <div className="action-buttons" style={{ marginLeft: 4, flexShrink: 0 }}>
              {(hasPermission('ver_clientes') || hasPermission('ver_movimientos') || hasPermission('ver_movimientos_clientes')) && (
                <button
                  type="button"
                  className={`btn-primary btn-wa-mini${!(cliente.celular || cliente.telefono) ? ' btn-wa-inactive' : ''}`}
                  onClick={abrirModalWhatsApp}
                  disabled={!(cliente.celular || cliente.telefono)}
                  title={
                    !(cliente.celular || cliente.telefono)
                      ? 'Este cliente no tiene teléfono o celular para WhatsApp'
                      : 'Enviar resumen de movimientos por WhatsApp (este cliente)'
                  }
                >
                  <span className="btn-wa-icon" aria-hidden="true">📱</span>
                </button>
              )}
              {hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{ clienteId: cliente.id }}
                  className="btn-action-icon"
                  title="Nuevo movimiento (débito)"
                >
                  <span aria-hidden="true">💵</span>
                </Link>
              )}
              {hasPermission('crear_creditos') && hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{ clienteId: cliente.id, nuevoRecibo: true }}
                  className="btn-action-icon"
                  title="Nuevo recibo de cobro"
                >
                  <span aria-hidden="true">🧾</span>
                </Link>
              )}
              {hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{ clienteId: cliente.id, nuevoPagoCliente: true }}
                  className="btn-action-icon"
                  title="Pago a cuenta (egreso desde fondos)"
                >
                  <span aria-hidden="true">💸</span>
                </Link>
              )}
              {hasPermission('editar_clientes') && (
                <Link
                  to={`/clientes/editar/${cliente.id}`}
                  className="btn-action-icon btn-action-edit"
                  title="Editar cliente"
                >
                  <span aria-hidden="true">✏️</span>
                </Link>
              )}
            </div>
          )}
        </div>

      <div className="listado-panel-cuerpo clientes-listado-cuerpo">
      <div className={`listado-tabla-area clientes-listado-tabla-area${refrescando ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {[
                { id: 'fecha', label: 'Fecha' },
                { id: 'concepto', label: 'Concepto' },
                { id: 'debito', label: 'Débito' },
                { id: 'credito', label: 'Crédito' },
                { id: 'saldo', label: 'Saldo acumulado' },
              ].map(col => (
                <th
                  key={col.id}
                  className="th-sortable"
                  onClick={() => handleSortColumn(col.id)}
                  title={`Ordenar por ${col.label}. Clic de nuevo invierte el orden.`}
                >
                  <span>{col.label}</span>
                  {sortByColumn === col.id && (
                    <span className="sort-indicator" aria-hidden="true">
                      {sortAsc ? ' ▲' : ' ▼'}
                    </span>
                  )}
                </th>
              ))}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="no-data">Cargando...</td>
              </tr>
            ) : movimientosParaMostrar.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">No hay movimientos en el período</td>
              </tr>
            ) : (
              movimientosParaMostrar.map(mov => (
                <tr key={mov.id}>
                  <td>{formatFecha(mov.fecha)}</td>
                  <td>
                    <MovimientoConceptoCell mov={mov} />
                  </td>
                  <td className="negative">
                    {mov.tipo_movimiento === 'DEBITO'
                      ? <strong>${formatMoneda(mov.monto)}</strong>
                      : '-'}
                  </td>
                  <td className="positive">
                    {mov.tipo_movimiento === 'CREDITO'
                      ? <strong>${formatMoneda(mov.monto)}</strong>
                      : '-'}
                  </td>
                  <td
                    className={
                      parseFloat(mov.saldo_acumulado || 0) > 0
                        ? 'negative'
                        : parseFloat(mov.saldo_acumulado || 0) < 0
                          ? 'positive'
                          : ''
                    }
                  >
                    <strong>${formatMoneda(mov.saldo_acumulado)}</strong>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn-action-icon"
                        title="Ver detalle del movimiento"
                        onClick={() => abrirDetalle(mov)}
                      >
                        <span aria-hidden="true">🔍</span>
                      </button>
                      {hasPermission('ver_movimientos') && (
                        <button
                          type="button"
                          className="btn-action-icon"
                          title={
                            movimientoTieneReciboGrupo(mov)
                              ? 'Imprimir recibo de cobro grupo'
                              : movimientoTieneRecibo(mov)
                                ? 'Imprimir recibo'
                                : 'Imprimir comprobante'
                          }
                          onClick={() => handleImprimirFila(mov)}
                        >
                          <span aria-hidden="true">🖨️</span>
                        </button>
                      )}
                      <FacturaMovimientoMarca mov={mov} puedePdf={hasPermission('ver_facturacion')} />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {refrescando && (
        <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
          <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
          <span className="clientes-listado-refresh-label">Actualizando…</span>
        </div>
      )}
      </div>
      </div>
      </div>

      <MovimientoDetalleModal
        mov={detalleMovimiento}
        recibo={detalleRecibo}
        reciboGrupo={detalleReciboGrupo}
        pago={detallePago}
        pagoError={detallePagoError}
        loading={detalleLoading}
        esProveedores={false}
        onClose={cerrarDetalle}
        formatFecha={formatFecha}
        formatFechaHora={formatFechaHora}
        hasPermission={hasPermission}
        onAnularRecibo={handleAnularRecibo}
        onAnularReciboGrupo={handleAnularReciboGrupo}
        onAnularPago={handleAnularPago}
        anulandoComprobante={anulandoComprobante}
        logoUrl={logoUrl}
      />

      {showModalWhatsApp && (
        <div className="modal-overlay" onClick={() => setShowModalWhatsApp(false)}>
          <div className="modal-content" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <h2>Enviar resumen de movimientos por WhatsApp</h2>
            <WhatsAppServicioPanel
              useBaileys={waUseBaileys}
              onToggleUseBaileys={handleToggleWaBaileys}
              status={waStatus}
              loading={waStatusLoading}
              onRefresh={refreshWaStatus}
            />
            <p style={{ marginBottom: 12, color: '#666' }}>
              Cliente: <strong>{cliente?.razon_social}</strong>. Destino: {cliente?.celular || cliente?.telefono}
            </p>
            <div style={{ marginBottom: 12 }}>
              <label>Saldo al (WhatsApp): </label>
              <input type="date" value={fechaSaldoWa} onChange={(e) => setFechaSaldoWa(e.target.value)} className="search-input" />
            </div>
            {plantillas.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label>Plantilla:</label>
                <select
                  value={plantillaSeleccionadaId}
                  onChange={e => seleccionarPlantilla(e.target.value)}
                  className="search-input"
                  style={{ marginLeft: 8 }}
                >
                  {plantillas.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
                <PlantillaImagenEditor
                  plantilla={plantillas.find((p) => p.id === plantillaSeleccionadaId)}
                  onChange={loadPlantillas}
                  useBaileys={waUseBaileys}
                  canEdit={hasPermission('editar_clientes')}
                />
              </div>
            )}
            <label>Mensaje (variables: razon_social, cuit, saldo, fecha_saldo, lista_movimientos, fecha_desde, fecha_hasta, tipo_nombre):</label>
            <textarea
              value={mensajePersonalizado}
              onChange={e => setMensajePersonalizado(e.target.value)}
              rows={12}
              className="search-input"
              style={{ width: '100%', marginTop: 6, marginBottom: 12 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={guardarEnHistorial}
                onChange={e => setGuardarEnHistorial(e.target.checked)}
              />
              Guardar en historial de envíos
            </label>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secondary" onClick={() => setShowModalWhatsApp(false)}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={enviarResumenWhatsApp}>
                {waUseBaileys ? 'Encolar mensaje por WhatsApp' : 'Abrir WhatsApp con mensaje'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClienteMovimientos;
